import { localities, zones, provinces } from "./data";
import type {
  LocalityRecord,
  ZoneRecord,
  ProvinceRecord,
  ResolvedReport,
  ResolverLayer,
} from "./types";

const NEARBY_CP_THRESHOLD = 20;

export function isValidArgentineCp(cp: string): boolean {
  return /^\d{4}$/.test(cp.trim());
}

interface ParsedRange {
  from: number;
  to: number;
}

function parseRange(range: string): ParsedRange | null {
  if (!range || typeof range !== "string") return null;
  const trimmed = range.trim();
  if (trimmed.includes("-")) {
    const [from, to] = trimmed.split("-").map((s) => parseInt(s.trim(), 10));
    if (Number.isNaN(from) || Number.isNaN(to)) return null;
    return { from, to };
  }
  const single = parseInt(trimmed, 10);
  if (Number.isNaN(single)) return null;
  return { from: single, to: single };
}

function cpInRange(cp: number, range: string): boolean {
  const parsed = parseRange(range);
  if (!parsed) return false;
  return cp >= parsed.from && cp <= parsed.to;
}

function rangeDistance(
  cp: number,
  range: string,
): { distance: number; nearestCp: number } | null {
  const parsed = parseRange(range);
  if (!parsed) return null;
  if (cp >= parsed.from && cp <= parsed.to) {
    return { distance: 0, nearestCp: cp };
  }
  const distFrom = Math.abs(cp - parsed.from);
  const distTo = Math.abs(cp - parsed.to);
  return distFrom <= distTo
    ? { distance: distFrom, nearestCp: parsed.from }
    : { distance: distTo, nearestCp: parsed.to };
}

function findLocality(cp: number): LocalityRecord | undefined {
  return localities.find((loc) => loc.cp_ranges.some((range) => cpInRange(cp, range)));
}

function findNearbyLocality(
  cp: number,
): { locality: LocalityRecord; distance: number; nearestCp: number } | undefined {
  const cpStr = String(cp).padStart(4, "0");
  const cpPrefix2 = cpStr.slice(0, 2);

  let best: { locality: LocalityRecord; distance: number; nearestCp: number } | undefined;

  for (const loc of localities) {
    for (const range of loc.cp_ranges) {
      const trimmed = range.trim();
      if (trimmed.slice(0, 2) !== cpPrefix2) continue;
      const result = rangeDistance(cp, trimmed);
      if (!result) continue;
      if (result.distance === 0) continue;
      if (result.distance > NEARBY_CP_THRESHOLD) continue;
      if (!best || result.distance < best.distance) {
        best = { locality: loc, ...result };
      }
    }
  }
  return best;
}

function findZone(cp: number): ZoneRecord | undefined {
  return zones.find((zone) => zone.cp_ranges.some((range) => cpInRange(cp, range)));
}

function findProvince(cp: number): ProvinceRecord | undefined {
  return provinces.find((p) => p.cp_ranges.some((range) => cpInRange(cp, range)));
}

export function resolveReport(cp: string): ResolvedReport | null {
  if (!isValidArgentineCp(cp)) return null;

  const cpNum = parseInt(cp, 10);

  const locality = findLocality(cpNum);
  if (locality) {
    return {
      layer: "locality",
      profile: locality.profile,
      locality,
      cp,
      resolvedName: `${locality.name}, ${locality.province}`,
    };
  }

  const nearby = findNearbyLocality(cpNum);
  if (nearby) {
    return {
      layer: "locality-nearby",
      profile: nearby.locality.profile,
      locality: nearby.locality,
      cp,
      resolvedName: `${nearby.locality.name}, ${nearby.locality.province}`,
      matchDistance: nearby.distance,
      matchedCp: String(nearby.nearestCp).padStart(4, "0"),
    };
  }

  const zone = findZone(cpNum);
  if (zone) {
    return { layer: "zone", profile: zone.profile, zone, cp, resolvedName: zone.name };
  }

  const province = findProvince(cpNum);
  if (province) {
    return {
      layer: "province",
      profile: province.default_profile,
      province,
      cp,
      resolvedName: province.name,
    };
  }

  return null;
}

export function describeLayer(layer: ResolverLayer): string {
  switch (layer) {
    case "locality":
      return "Datos del prestador de tu zona";
    case "locality-nearby":
      return "Datos del prestador de un CP cercano";
    case "zone":
      return "Datos regionales de tu cuenca o acuífero";
    case "province":
      return "Información provincial orientativa";
  }
}
